import { IconFlame } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClassicLoader from "@/components/common/classic-loader";
import type { CartPricing } from "@/features/cart/types";
import type { LengthDiscount } from "@/features/diets/types/length-discount.type";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn, formatPrice } from "@/lib/utils";

type Props = {
  lengthDiscount: LengthDiscount;
  currentDays: number;
  pricing?: CartPricing;
  loading?: boolean;
  setDays?: (days: number) => void;
};

export const LengthDiscountStepper: React.FC<Props> = ({
  lengthDiscount,
  currentDays,
  pricing,
  loading,
  setDays,
}) => {
  const t = useTranslations();
  const { isMobile } = useMobile();
  const { discounts } = lengthDiscount;

  // State to track animated days value
  const [animatedDays, setAnimatedDays] = useState(currentDays);
  const animationRef = useRef<number | undefined>(undefined);

  // Smooth easing function
  const easeOutCubic = useCallback((t: number): number => {
    return 1 - (1 - t) ** 3;
  }, []);

  // Handle setting days with smooth animation
  const handleSetDays = useCallback(
    (days: number) => setDays?.(days),
    [setDays],
  );
  const maxAvailableDiscount = useMemo(() => {
    const maxDiscountTier = discounts[discounts.length - 1];
    const priceBeforeDiscount = pricing?.before?.gross;

    if (!maxDiscountTier || !priceBeforeDiscount) {
      return 0;
    }

    if (currentDays <= 0) {
      return 0;
    }

    const dailyPriceBeforeDiscount = priceBeforeDiscount / currentDays;

    return (
      dailyPriceBeforeDiscount *
      maxDiscountTier.days *
      (maxDiscountTier.discount / 100)
    );
  }, [currentDays, discounts, pricing?.before?.gross]);

  // Smooth animation for days counter
  useEffect(() => {
    // Only animate if currentDays is different from animatedDays
    if (currentDays === animatedDays) return;

    const startValue = animatedDays;
    const targetValue = currentDays;
    const difference = targetValue - startValue;
    const duration = 400; // 0.4 seconds for smooth animation
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Apply easing function
      const easedProgress = easeOutCubic(progress);
      const currentValue = startValue + difference * easedProgress;
      const roundedValue = Math.round(currentValue);

      setAnimatedDays(roundedValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimatedDays(targetValue);
      }
    };

    // Cancel any existing animation
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [currentDays, animatedDays, easeOutCubic]);

  // Calculate all values that depend on animatedDays - reactive to changes
  const { stepPositions, progressPercentage } = useMemo((): {
    stepPositions: number[];
    progressPercentage: number;
  } => {
    // Calculate step positions based on number of discounts
    const getStepPositions = () => {
      const lastStep = isMobile ? 85 : 97.5;
      const middleStep = isMobile ? 50 : 56.25;
      const numSteps = discounts.length;
      if (numSteps === 1) return [lastStep]; // Near end with margin
      if (numSteps === 2) return [15, lastStep]; // Start and end with margins
      if (numSteps === 3) return [15, middleStep, lastStep]; // Start, middle, end with margins
      // For more than 3 steps, distribute evenly
      return discounts.map((_, index) => 15 + (index / (numSteps - 1)) * 70);
    };

    const stepPositions = getStepPositions();

    // Calculate progress percentage
    let progressPercentage = 0;
    if (discounts.length > 0) {
      const firstStepDays = discounts[0]?.days;
      const lastStepDays = discounts[discounts.length - 1]?.days;

      if (
        firstStepDays &&
        lastStepDays &&
        stepPositions[0] &&
        stepPositions[stepPositions.length - 1]
      ) {
        // If reached maximum tier, show 100%
        if (animatedDays >= lastStepDays) {
          progressPercentage = 100;
        } else if (animatedDays < firstStepDays) {
          // Before first step - progress from 0 to first step position
          progressPercentage =
            (animatedDays / firstStepDays) * stepPositions[0];
        } else {
          // After first step - find which step range we're in and calculate progress within that range
          let currentStepIndex = 0;
          for (let i = 0; i < discounts.length - 1; i++) {
            const currentStepDays = discounts[i]?.days;
            const nextStepDays = discounts[i + 1]?.days;
            if (
              currentStepDays &&
              nextStepDays &&
              animatedDays >= currentStepDays &&
              animatedDays < nextStepDays
            ) {
              currentStepIndex = i;
              break;
            }
          }

          const currentStepDays = discounts[currentStepIndex]?.days;
          const nextStepDays = discounts[currentStepIndex + 1]?.days;
          const currentStepPosition = stepPositions[currentStepIndex];
          const nextStepPosition = stepPositions[currentStepIndex + 1];

          if (
            currentStepDays &&
            nextStepDays &&
            currentStepPosition !== undefined &&
            nextStepPosition !== undefined
          ) {
            // Calculate progress within the current step range
            const rangeProgress =
              (animatedDays - currentStepDays) /
              (nextStepDays - currentStepDays);
            progressPercentage =
              currentStepPosition +
              rangeProgress * (nextStepPosition - currentStepPosition);
          } else {
            // Fallback to old calculation if something goes wrong
            progressPercentage =
              stepPositions[0] +
              ((animatedDays - firstStepDays) /
                (lastStepDays - firstStepDays)) *
                (stepPositions[stepPositions.length - 1]! - stepPositions[0]);
          }
        }
      }
    }

    return {
      stepPositions,
      progressPercentage,
    };
  }, [animatedDays, discounts, isMobile]);

  const DiscountMessage = () => {
    // Use currentDays for instant updates instead of animatedDays
    const instantCurrentTier = discounts
      .filter((d) => d.days <= currentDays)
      .sort((a, b) => b.days - a.days)[0];
    const instantNextTier = discounts.find((d) => d.days > currentDays);
    const instantDaysToNext = instantNextTier
      ? instantNextTier.days - currentDays
      : 0;

    if (
      instantNextTier &&
      instantNextTier.days === discounts[discounts.length - 1]?.days
    ) {
      return (
        <span className="text-brand-green2-300">
          {t.rich("cart.configuration.lengthDiscounts.addDaysMax", {
            days: () => (
              <span className="font-bold">
                {t("common.days", { count: instantDaysToNext })}
              </span>
            ),
            discount: () => (
              <span className="font-bold">
                {instantNextTier.discount ?? 0}%
              </span>
            ),
          })}
        </span>
      );
    } else if (instantNextTier) {
      return (
        <span className="text-brand-green2-300">
          {t.rich("cart.configuration.lengthDiscounts.addDays", {
            days: () => (
              <span className="font-bold">
                {t("common.days", { count: instantDaysToNext })}
              </span>
            ),
            discount: () => (
              <span className="font-bold">
                {instantNextTier.discount ?? 0}%
              </span>
            ),
          })}
        </span>
      );
    } else {
      return (
        <span className="text-brand-green2-300">
          {t.rich("cart.configuration.lengthDiscounts.maxDiscount", {
            discount: () => (
              <span className="font-bold">
                {instantCurrentTier?.discount ?? 0}%
              </span>
            ),
          })}
        </span>
      );
    }
  };

  const TotalSavings = useMemo(() => {
    const isMaxDiscountReached =
      discounts.find((d) => d.days > currentDays) === undefined;

    const totalSavingsLabel = isMaxDiscountReached
      ? t("cart.configuration.lengthDiscounts.totalSavings")
      : t("cart.configuration.lengthDiscounts.save");

    return (
      <span className={cn("font-bold", loading && "flex items-center gap-x-2")}>
        <span>{totalSavingsLabel} </span>
        {loading ? (
          <ClassicLoader className="size-4" />
        ) : (
          <span className="text-brand-red">
            {formatPrice(maxAvailableDiscount)}
          </span>
        )}
      </span>
    );
  }, [loading, t, maxAvailableDiscount, discounts, currentDays]);

  const ProgressLine = () => (
    <>
      <div className="absolute top-1/2 right-0 left-0 h-2 -translate-y-1/2 transform rounded-full bg-brand-grey1" />
      <div
        className="absolute top-1/2 left-0 h-2 -translate-y-1/2 transform rounded-full bg-gradient-to-r from-brand-yellow to-brand-red"
        style={{
          width: `${progressPercentage}%`,
        }}
      />
    </>
  );

  const StepCircle = ({
    isActive,
    discount,
  }: {
    isActive: boolean;
    discount: LengthDiscount["discounts"][number];
  }) => {
    return (
      <div className="absolute -translate-x-1/2 transform">
        <div
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-full border-3 bg-brand-white transition-all duration-100 hover:cursor-pointer lg:h-[45px] lg:w-[45px]",
            isActive
              ? "border-brand-red text-brand-red"
              : "border-brand-grey4 text-brand-grey5 hover:scale-105 hover:text-brand-green1-300",
          )}
          onClick={() => handleSetDays(discount.days)}
        >
          <span className="font-bold text-xs lg:text-sm">
            -{discount.discount ?? 0}%
          </span>
        </div>
      </div>
    );
  };

  const Steps = () => {
    return discounts.map((discount, index) => {
      const isActive = animatedDays >= discount.days;
      const position = stepPositions[index];
      return (
        <div
          key={index}
          className="absolute -translate-x-1/2 transform"
          style={{ left: `${position}%` }}
        >
          <StepCircle isActive={isActive} discount={discount} />
        </div>
      );
    });
  };

  return (
    <div className="mx-auto w-full max-w-4xl rounded-2xl bg-background p-4 lg:space-y-[18px]">
      {/* Header */}
      <div className="rounded-xl border border-transparent bg-brand-red/10 p-4 text-brand-green2-300">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <IconFlame
              className="size-4 flex-shrink-0 text-brand-red"
              stroke={1.5}
            />
            <h2 className="font-medium text-lg">
              {t("cart.configuration.lengthDiscounts.title")}
            </h2>
          </div>
          <p className="font-normal text-sm">
            {t("cart.configuration.lengthDiscounts.description")}
          </p>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="block max-lg:mt-4 lg:hidden">
        <div>
          <span>
            <DiscountMessage />
          </span>
          <div className="mt-1">{TotalSavings}</div>
        </div>

        {/* Mobile Horizontal Stepper */}
        <div className="relative mt-4 h-12">
          <ProgressLine />
          <Steps />
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden space-y-[18px] lg:block">
        <div className="flex items-center justify-between">
          <DiscountMessage />
          {TotalSavings}
        </div>

        {/* Desktop Horizontal Stepper */}
        <div className="relative h-[45px]">
          <ProgressLine />
          <Steps />
        </div>
      </div>
    </div>
  );
};
